import styled, { css } from 'styled-components'
import { ReactComponent as ChevronRightIcon } from '@sketch/icons/chevron-right-17'
import { ReactComponent as Ellipsis } from '@sketch/icons/ellipsis-17'
import { ReactComponent as LoadingSpinnerIcon } from '@sketch/icons/img-loader-11'
import { ButtonUnstyled, Dropdown } from '@sketch/components'

const ITEM_BASE_OFFSET = 5 // Base offset for the first level in px
const ITEM_LEVEL_OFFSET = 15 // Offset per level in px
const ITEM_ICON_WIDTH = 25 // Width of the icon container in px

function getItemLeftMargin(level: number): number {
  return ITEM_BASE_OFFSET + level * ITEM_LEVEL_OFFSET
}

export const VirtualLayerItem = styled.li<{
  $level: number
  $isHovered: boolean
  $isSymbol?: boolean
}>`
  /* TanStack virtual styles */
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  ${({ theme, $isHovered, $level, $isSymbol }) => css`
    display: flex;
    align-items: center;
    gap: 4px;
    padding-left: ${getItemLeftMargin($level)}px;
    font-size: ${theme.fontSizes.D};

    cursor: pointer;
    user-select: none;
    height: 28px;
    border-radius: ${theme.radii.large};

    &[aria-selected='true'] {
      ${$isSymbol
        ? css`
            background-color: ${theme.colors.inspect.component.A};
            color: ${theme._name === 'dark'
              ? theme.colors.foreground.secondary.A
              : theme.colors.foreground.primary.A};
          `
        : css`
            background-color: ${theme.colors.sketch.A};
            color: ${theme._name === 'dark'
              ? theme.colors.foreground.secondary.A
              : theme.colors.foreground.primary.A};
          `}
    }

    ${$isHovered &&
    css`
      :not([aria-selected='true']) {
        background-color: ${theme.colors.state.hover};
        color: ${theme.colors.foreground.secondary.B};
      }
    `}
  `};
`

export const VerticalLine = styled.div<{ level: number }>`
  ${({ level }) => css`
    width: 1px;
    /* Re-centering the line */
    transform: translateX(-50%);
    height: 100%;
    left: ${getItemLeftMargin(level) + ITEM_ICON_WIDTH / 2}px;
    background-color: #979797;
    opacity: 0.2;
    position: absolute;
  `}
`

export const ItemIconContainer = styled.div`
  min-width: 24px; /* It can be more than 24px when masks layers icon is present */
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`

export const ToggleButton = styled(ButtonUnstyled)`
  border-radius: ${({ theme }) => theme.radii.small};
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: currentColor;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.tertiary.C};
  }

  [aria-selected='true'] &:hover {
    background-color: ${({ theme }) => theme.colors.sketch.F};
  }
`

type ToggleIconProps = {
  $isCollapsed: boolean
}
export const ToggleIcon = styled(ChevronRightIcon)<ToggleIconProps>`
  transform: rotate(${({ $isCollapsed }) => ($isCollapsed ? '0deg' : '90deg')});
  transition: ${({ theme }) =>
    `transform ${theme.transitions.duration[2]} ${theme.transitions.timing.easeOutExponential}`};
  height: 17px;
  width: 17px;
`

export const LayerName = styled.p<{ $isSymbol?: boolean }>`
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  color: ${({ theme, $isSymbol }) =>
    $isSymbol ? theme.colors.inspect.component.A : 'inherit'};

  [aria-selected='true'] & {
    /* When a symbol is selected we don't change the text color */
    color: inherit;
  }
`

type EllipsisButtonProps = {
  $isVisible: boolean
}

export const EllipsisButton = styled(ButtonUnstyled)<EllipsisButtonProps>`
  ${({ theme, $isVisible }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${$isVisible ? '40px' : '0px'};
    opacity: ${$isVisible ? 1 : 0};

    cursor: pointer;
    border-radius: ${theme.radii.small};
    height: 28px;
    background-color: ${theme.colors.background.tertiary.C};
    color: ${theme.colors.foreground.secondary.D};
    transition: ${css`
      opacity ${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut},
      width ${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}
    `};

    [aria-selected='true'] & {
      color: inherit;
    }
  `}
`

export const EllipsisIcon = styled(Ellipsis)`
  width: 17px;
  height: 17px;
`

export const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

export const LoadingSpinner = styled(LoadingSpinnerIcon)`
  width: 11px;
  height: 11px;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  animation: spin 1s linear infinite;
`

export const MenuIcon = styled.svg`
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const StyledDropdownItem = styled(Dropdown.Item)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
`
